import React, { useEffect, useState, useRef, useCallback } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { useDispatch, useSelector } from "react-redux";
import {
  submitEnquiryForm,
  resetEnquiryFormState,
} from "../../store/slices/enquiryFormSlice";
import {
  Code2,
  Database,
  Layers,
  Smartphone,
  Search,
  BrainCircuit,
  ShieldCheck,
  Palette,
  CheckCircle2,
  Send,
  MessageSquare,
  Loader2,
  AlertCircle,
} from "lucide-react";
import ReCAPTCHA from "react-google-recaptcha";
import "./ItProduct.css";
import CreativeBadgeCloud from "../../common/languages-badge";

const ItProduct = () => {
  const dispatch = useDispatch();

  const { loading, error, success } = useSelector(
    (state) => state.enquiryForm || {},
  );

  const [formData, setFormData] = useState({
    clientName: "",
    clientEmail: "",
    clientPhone: "",
    serviceType: "",
    budget: "",
    referenceName: "",
    referenceEmail: "",
    projectDetails: "",
    agreeToTerms: false,
  });

  const [errors, setErrors] = useState({});
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [globalError, setGlobalError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [captchaToken, setCaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);

  // Move services outside component or memoize it
  const services = useRef([
    {
      title: "Frontend Development",
      icon: <Code2 size={32} />,
      desc: "We specialize in creating stunning, high-performance frontend applications using modern frameworks like React, Next.js, Vue.js, and Angular.",
      points: [
        "Blazing Fast Performance",
        "SEO-Friendly Architecture",
        "Cross-Browser Compatibility",
        "Maintainable Codebase",
        "Modern UI/UX",
      ],
      img: "./images/it-solution/front.png",
    },
    {
      title: "Backend Development",
      icon: <Database size={32} />,
      desc: "Our backend development services focus on building secure, scalable, and high-performance server-side applications.",
      points: [
        "Scalable Infrastructure",
        "High Performance",
        "Secure Data Handling",
        "Easy Integration",
        "Reliable Uptime",
      ],
      img: "./images/it-solution/back.png",
    },
    {
      title: "UI/UX Design",
      icon: <Layers size={32} />,
      desc: "Great design is more than just aesthetics; it's about how it works. Our UI/UX design team creates intuitive, user-friendly interfaces.",
      points: [
        "Higher Conversion Rates",
        "Improved User Satisfaction",
        "Lower Support Costs",
        "Stronger Brand Identity",
        "Intuitive Navigation",
      ],
      img: "./images/it-solution/uiux.png",
    },
    {
      title: "App Development",
      icon: <Smartphone size={32} />,
      desc: "We build high-performance, feature-rich mobile applications for iOS and Android platforms using Flutter or React Native.",
      points: [
        "Enhanced Customer Engagement",
        "Direct Marketing Channel",
        "Increased Brand Recognition",
        "Better Customer Loyalty",
        "Competitive Advantage",
      ],
      img: "./images/it-solution/app.png",
    },
    {
      title: "SEO & Digital Marketing",
      icon: <Search size={32} />,
      desc: "In the digital age, visibility is everything. Our comprehensive digital marketing services are designed to boost your online presence.",
      points: [
        "Increased Brand Awareness",
        "Targeted Lead Generation",
        "Measurable ROI",
        "Improved Customer Engagement",
        "Higher Conversion Rates",
      ],
      img: "./images/it-solution/soe.png",
    },
    {
      title: "AI & ML Solutions",
      icon: <BrainCircuit size={32} />,
      desc: "Artificial Intelligence is transforming the IT industry. Our AI & Machine Learning solutions help businesses automate workflows.",
      points: [
        "Improved Operational Efficiency",
        "Smarter Decision Making",
        "Reduced Manual Effort",
        "Scalable AI Solutions",
        "Competitive Advantage",
      ],
      img: "./images/it-solution/ai.png",
    },
    {
      title: "Testing & QA",
      icon: <ShieldCheck size={32} />,
      desc: "Rigorous quality standards ensuring a smooth launch. We identify issues before they reach your users.",
      points: [
        "Higher Software Quality",
        "Reduced Post-Launch Issues",
        "Faster Release Cycles",
      ],
      img: "./images/it-solution/testing.png",
    },
    {
      title: "Graphic Design",
      icon: <Palette size={32} />,
      desc: "Strategic visuals that resonate. From logos to social media graphics, we bring your brand to life.",
      points: [
        "Strong Brand Recognition",
        "Professional Appearance",
        "Consistent Visual Identity",
      ],
      img: "./images/it-solution/graphic.png",
    },
  ]);

  // ✅ FIXED: Initialize AOS with better configuration
  useEffect(() => {
    // Initialize AOS after component mounts
    AOS.init({
      duration: 800,
      once: true,
      offset: 150,
      easing: "ease-out-cubic",
      disable: false,
      startEvent: "DOMContentLoaded",
      initClassName: "aos-init",
      animatedClassName: "aos-animate",
      useClassNames: false,
      disableMutationObserver: false,
      debounceDelay: 50,
      throttleDelay: 99,
    });

    // Create a ref for the refresh function
    const refreshAOS = () => {
      AOS.refresh();
    };

    // Add event listener
    window.addEventListener("load", refreshAOS);

    // Cleanup
    return () => {
      window.removeEventListener("load", refreshAOS);
    };
  }, []); // Empty dependency array - runs once on mount

  // ✅ FIXED: Remove the problematic useEffect or use it with proper dependencies
  // Only refresh AOS when submitSuccess changes (if needed)
  useEffect(() => {
    if (submitSuccess) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        AOS.refresh();
      }, 100);
    }
  }, [submitSuccess]);

  // ✅ SUCCESS HANDLE
  useEffect(() => {
    if (success) {
      setSubmitSuccess(true);
      setIsSubmitting(false);

      setFormData({
        clientName: "",
        clientEmail: "",
        clientPhone: "",
        serviceType: "",
        budget: "",
        referenceName: "",
        referenceEmail: "",
        projectDetails: "",
        agreeToTerms: false,
      });

      setCaptchaToken(null);
      if (recaptchaRef.current) recaptchaRef.current.reset();

      dispatch(resetEnquiryFormState());
    }
  }, [success, dispatch]);

  // --- Handlers ---
  const handleChange = (e) => {
    const { id, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: type === "checkbox" ? checked : value,
    }));

    if (errors[id]) setErrors((prev) => ({ ...prev, [id]: null }));
    if (globalError) setGlobalError("");
  };

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
    if (errors.captcha) setErrors((prev) => ({ ...prev, captcha: null }));
  };

  // --- Validation ---
  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.clientName.trim()) newErrors.clientName = "Name is required";
    if (
      !formData.clientEmail.trim() ||
      !emailRegex.test(formData.clientEmail)
    ) {
      newErrors.clientEmail = "Valid email is required";
    }
    if (!formData.clientPhone.trim())
      newErrors.clientPhone = "Phone number is required";
    if (!formData.serviceType)
      newErrors.serviceType = "Please select a service";
    if (!formData.budget.trim()) newErrors.budget = "Budget is required";
    if (!formData.referenceName.trim())
      newErrors.referenceName = "Reference name is required";
    if (
      !formData.referenceEmail.trim() ||
      !emailRegex.test(formData.referenceEmail)
    ) {
      newErrors.referenceEmail = "Valid email is required";
    }
    if (!formData.projectDetails.trim())
      newErrors.projectDetails = "Please provide project details";
    if (!formData.agreeToTerms)
      newErrors.agreeToTerms = "You must agree to the terms";
    if (!captchaToken) newErrors.captcha = "Please complete the reCAPTCHA";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ✅ SUBMIT (SAGA CALL)
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    const payload = {
      fullname: formData.clientName,
      email: formData.clientEmail,
      mobile: formData.clientPhone,
      serviceType: formData.serviceType,
      budget: formData.budget,
      projectDetails: formData.projectDetails,
      referenceName: formData.referenceName,
      referenceEmail: formData.referenceEmail,
      agreeToTerms: formData.agreeToTerms,
      recaptchaToken: captchaToken,
    };

    dispatch(submitEnquiryForm(payload));
  };

  return (
    <div className="krishna-it-container">
      {/* Hero Section */}
      <header className="kr-hero-section">
        <div className="kr-badge-pill" data-aos="fade-down">
          OUR EXPERTISE
        </div>
        <h1 className="kr-main-title" data-aos="fade-up" data-aos-delay="100">
          Modern IT <span className="kr-gradient-text">Solutions</span>
        </h1>
        <div
          className="kr-fancy-divider"
          data-aos="zoom-in"
          data-aos-delay="200"
        >
          <span></span>
          <div className="kr-dot"></div>
          <span></span>
        </div>
        <p className="kr-hero-subtext" data-aos="fade-up" data-aos-delay="300">
          Strategic digital transformations tailored to scale your enterprise
        </p>
        <div className="kr-bg-blur-circle"></div>
      </header>

      {/* Alternating Services */}
      <section className="kr-services-list">
        {services.current.map((item, idx) => {
          const isReversed = idx % 2 !== 0;
          return (
            <div
              className={`kr-service-block ${isReversed ? "kr-reverse" : ""}`}
              key={idx}
            >
              <div
                className="kr-text-side"
                data-aos={isReversed ? "fade-left" : "fade-right"}
                data-aos-duration="800"
                data-aos-delay={idx * 100}
              >
                <div className="kr-icon-blob">{item.icon}</div>
                <h2 className="kr-service-name">{item.title}</h2>
                <p className="kr-service-info">{item.desc}</p>
                <ul className="kr-points-list">
                  {item.points.map((pt, pIdx) => (
                    <li key={pIdx}>
                      <CheckCircle2 size={18} className="kr-check" />
                      {pt}
                    </li>
                  ))}
                </ul>
              </div>
              <div
                className="kr-visual-side"
                data-aos={isReversed ? "fade-right" : "fade-left"}
                data-aos-duration="800"
                data-aos-delay={idx * 100 + 100}
              >
                <div className="kr-image-wrapper">
                  <div className="kr-image-overlay"></div>
                  <img
                    src={item.img}
                    alt={item.title}
                    className="kr-service-img"
                  />
                </div>
                <div className="kr-deco-dots"></div>
                <div className="kr-deco-circle"></div>
              </div>
            </div>
          );
        })}
      </section>

      <section>
        <CreativeBadgeCloud />
      </section>

      {/* --- Inquiry Form CTA Section --- */}
      <section className="kr-cta-section" data-aos="fade-up">
        <div className="kr-cta-container">
          <div
            className="kr-cta-text"
            data-aos="fade-right"
            data-aos-delay="100"
          >
            <div className="kr-cta-icon-wrapper">
              <MessageSquare size={36} color="#4f46e5" />
            </div>
            <h2 className="kr-cta-heading">
              Ready to Transform Your{" "}
              <span className="kr-gradient-text">Business?</span>
            </h2>
            <p className="kr-cta-subheading">
              Whether you need a cutting-edge website, a robust CRM system, or
              an AI-powered workflow, our team is ready to build it.
            </p>
          </div>

          <div
            className="kr-cta-form-wrapper"
            data-aos="fade-left"
            data-aos-delay="200"
          >
            {submitSuccess ? (
              <div className="kr-success-message">
                <div className="kr-success-icon">
                  <CheckCircle2 size={64} color="#10b981" />
                </div>
                <h3>Inquiry Sent Successfully!</h3>
                <p>
                  Thank you for reaching out. Our team will get back to you
                  shortly.
                </p>
                <button
                  className="kr-reset-btn"
                  onClick={() => setSubmitSuccess(false)}
                >
                  Send Another Inquiry
                </button>
              </div>
            ) : (
              <form
                className="kr-inquiry-form"
                onSubmit={handleSubmit}
                noValidate
              >
                {globalError && (
                  <div className="kr-global-error">
                    <AlertCircle size={20} />
                    <span>{globalError}</span>
                  </div>
                )}

                <div className="kr-form-row">
                  <div className="kr-input-box">
                    <input
                      type="text"
                      id="clientName"
                      value={formData.clientName}
                      onChange={handleChange}
                      placeholder=" "
                    />
                    <label htmlFor="clientName">Full Name</label>
                    <div className="kr-input-line"></div>
                    {errors.clientName && (
                      <span className="kr-field-error">
                        {errors.clientName}
                      </span>
                    )}
                  </div>
                  <div className="kr-input-box">
                    <input
                      type="email"
                      id="clientEmail"
                      value={formData.clientEmail}
                      onChange={handleChange}
                      placeholder="loansviakrishna@gmail.com"
                    />
                    <label htmlFor="clientEmail">Email Address</label>
                    <div className="kr-input-line"></div>
                    {errors.clientEmail && (
                      <span className="kr-field-error">
                        {errors.clientEmail}
                      </span>
                    )}
                  </div>
                </div>

                <div className="kr-form-row">
                  <div className="kr-input-box">
                    <input
                      type="tel"
                      id="clientPhone"
                      value={formData.clientPhone}
                      onChange={handleChange}
                      placeholder="9737304440"
                    />
                    <label htmlFor="clientPhone">Phone Number</label>
                    <div className="kr-input-line"></div>
                    {errors.clientPhone && (
                      <span className="kr-field-error">
                        {errors.clientPhone}
                      </span>
                    )}
                  </div>
                  <div className="kr-input-box">
                    <select
                      id="serviceType"
                      value={formData.serviceType}
                      onChange={handleChange}
                    >
                      <option value="" disabled hidden></option>
                      <option value="website">Website Development</option>
                      <option value="crm">CRM & ERP Solutions</option>
                      <option value="app">Mobile App Development</option>
                      <option value="ai">AI & Machine Learning</option>
                      <option value="other">Other IT Services</option>
                    </select>
                    <label htmlFor="serviceType">Service Required</label>
                    <div className="kr-input-line"></div>
                    {errors.serviceType && (
                      <span className="kr-field-error">
                        {errors.serviceType}
                      </span>
                    )}
                  </div>
                </div>

                <div className="kr-input-box">
                  <input
                    type="text"
                    id="budget"
                    value={formData.budget}
                    onChange={handleChange}
                    placeholder=" "
                  />
                  <label htmlFor="budget">Budget</label>
                  <div className="kr-input-line"></div>
                  {errors.budget && (
                    <span className="kr-field-error">{errors.budget}</span>
                  )}
                </div>

                <div className="kr-form-row">
                  <div className="kr-input-box">
                    <input
                      type="text"
                      id="referenceName"
                      value={formData.referenceName}
                      onChange={handleChange}
                      placeholder=" "
                    />
                    <label htmlFor="referenceName">Reference Name</label>
                    <div className="kr-input-line"></div>
                    {errors.referenceName && (
                      <span className="kr-field-error">
                        {errors.referenceName}
                      </span>
                    )}
                  </div>
                  <div className="kr-input-box">
                    <input
                      type="email"
                      id="referenceEmail"
                      value={formData.referenceEmail}
                      onChange={handleChange}
                      placeholder=" "
                    />
                    <label htmlFor="referenceEmail">Reference E-mail</label>
                    <div className="kr-input-line"></div>
                    {errors.referenceEmail && (
                      <span className="kr-field-error">
                        {errors.referenceEmail}
                      </span>
                    )}
                  </div>
                </div>

                <div className="kr-input-box kr-textarea-box">
                  <textarea
                    id="projectDetails"
                    value={formData.projectDetails}
                    onChange={handleChange}
                    rows="4"
                    placeholder=" "
                  ></textarea>
                  <label htmlFor="projectDetails">
                    Tell us about your project
                  </label>
                  <div className="kr-input-line"></div>
                  {errors.projectDetails && (
                    <span className="kr-field-error">
                      {errors.projectDetails}
                    </span>
                  )}
                </div>

                <div className="kr-terms-row">
                  <input
                    type="checkbox"
                    id="agreeToTerms"
                    checked={formData.agreeToTerms}
                    onChange={handleChange}
                  />
                  <label htmlFor="agreeToTerms">
                    I agree to receive promotional updates via WhatsApp/SMS and
                    accept the{" "}
                    <a
                      href="/privacy-security-policy"
                      target="_blank"
                      rel="noreferrer"
                      className="kr-terms-link"
                    >
                      Terms & Conditions
                    </a>
                    . *
                  </label>
                  {errors.agreeToTerms && (
                    <span className="kr-error-text terms-error">
                      {errors.agreeToTerms}
                    </span>
                  )}
                </div>

                {/* reCAPTCHA */}
                <div className="kr-captcha-container">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey="6Ldk_IksAAAAAO8Aq02xgOPeBlszLT2K30hdJCjM"
                    onChange={handleCaptchaChange}
                  />
                </div>

                {errors.captcha && (
                  <span
                    className="kr-field-error"
                    style={{
                      marginTop: "-20px",
                      marginBottom: "20px",
                      display: "block",
                    }}
                  >
                    {errors.captcha}
                  </span>
                )}

                <button
                  type="submit"
                  className="kr-cta-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span>Sending...</span>
                      <Loader2 size={18} className="kr-spinner" />
                    </>
                  ) : (
                    <>
                      <span>Send Inquiry</span>
                      <Send size={18} />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ItProduct;
